import { useEffect, useState, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';

import { bootstrapSession, logout as logoutRequest } from '@/api/adapters/auth';
import { useAuthStore } from '@/app/store/auth.store';

// PRD 00 §3.1.D — idle session timeout: 60 minutes, automatic logout on inactivity (FEATURE_005).
const SESSION_TIMEOUT_MS = 60 * 60 * 1000;
const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'click',
];

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Re-establishes the session on a hard reload and runs the 60-min idle timer. The ID token lives in memory
 * only (never browser storage), so every page load starts signed-out; before rendering the app we silently
 * ask the backend to restore the session from the HttpOnly refresh cookie (FEATURE_005). A spinner shows
 * until that resolves — never a flash of the login screen. No-op cost in mock dev (bootstrapSession returns
 * null immediately). Interceptors are installed in main.tsx before this runs.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const login = useAuthStore((s) => s.login);
  const logout = useAuthStore((s) => s.logout);
  const [booting, setBooting] = useState(true);

  useEffect(() => {
    let active = true;
    void bootstrapSession().then((user) => {
      if (!active) return;
      if (user) {
        // The Firebase SDK now holds the (auto-refreshing) ID token; the stored token is vestigial.
        login('', user.email, user.name, user.role);
      }
      setBooting(false);
    });
    return () => {
      active = false;
    };
  }, [login]);

  // 60-min idle auto-logout: revoke the server-side refresh cookie + drop the Firebase session
  // (logoutRequest) so a reload after the timeout cannot re-establish, then clear local auth.
  useEffect(() => {
    if (!isAuthenticated) return;
    let timer: ReturnType<typeof setTimeout>;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        // Tell the server this was a 60-min idle timeout so it audits LOGOUT_SESSION_TIMEOUT, not a user logout.
        void logoutRequest({ reason: 'SESSION_TIMEOUT' });
        logout();
      }, SESSION_TIMEOUT_MS);
    };
    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, reset, { passive: true }),
    );
    reset();
    return () => {
      clearTimeout(timer);
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, reset));
    };
  }, [isAuthenticated, logout]);

  if (booting) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
      </div>
    );
  }

  return <>{children}</>;
}
